import { Variants } from 'framer-motion';

// Page transition variants
export const pageTransitions: Variants = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.2,
      ease: 'easeIn'
    }
  }
};

// Fade transition
export const fadeTransition: Variants = {
  initial: { opacity: 0 },
  animate: { 
    opacity: 1,
    transition: { duration: 0.3 }
  },
  exit: { 
    opacity: 0,
    transition: { duration: 0.2 }
  }
};

// Slide transitions
export const slideFromRight: Variants = {
  initial: { x: '100%', opacity: 0 },
  animate: { 
    x: 0, 
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30
    }
  },
  exit: { 
    x: '-100%', 
    opacity: 0,
    transition: { duration: 0.3 }
  }
};

export const slideFromLeft: Variants = {
  initial: { x: '-100%', opacity: 0 },
  animate: { 
    x: 0, 
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30
    }
  },
  exit: { 
    x: '100%', 
    opacity: 0,
    transition: { duration: 0.3 }
  }
};

// Scale transitions
export const scaleTransition: Variants = {
  initial: { scale: 0.9, opacity: 0 },
  animate: { 
    scale: 1, 
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 500,
      damping: 30
    }
  },
  exit: { 
    scale: 0.9, 
    opacity: 0,
    transition: { duration: 0.2 }
  }
};

// Modal/Dialog animations
export const modalTransition: Variants = {
  initial: { 
    scale: 0.95, 
    opacity: 0,
    y: 20
  },
  animate: { 
    scale: 1, 
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 25
    }
  },
  exit: { 
    scale: 0.95, 
    opacity: 0,
    y: 20,
    transition: { duration: 0.2 }
  }
};

// Backdrop animation
export const backdropTransition: Variants = {
  initial: { opacity: 0 },
  animate: { 
    opacity: 1,
    transition: { duration: 0.2 }
  },
  exit: { 
    opacity: 0,
    transition: { duration: 0.15 }
  }
};

// Stagger children animations
export const staggerContainer: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

export const staggerItem: Variants = {
  initial: { opacity: 0, y: 20 },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30
    }
  }
};

// Toolbar animations
export const toolbarTransition: Variants = {
  initial: { y: -100, opacity: 0 },
  animate: { 
    y: 0, 
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30,
      delay: 0.1
    }
  },
  exit: { 
    y: -100, 
    opacity: 0,
    transition: { duration: 0.2 }
  }
};

// Button hover animations
export const buttonHover = {
  whileHover: { 
    scale: 1.05,
    transition: { duration: 0.2 }
  },
  whileTap: { 
    scale: 0.95,
    transition: { duration: 0.1 }
  }
};

// Tool selection animation
export const toolSelectTransition: Variants = {
  initial: { scale: 0, opacity: 0 },
  animate: { 
    scale: 1, 
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 500,
      damping: 30
    }
  },
  exit: { 
    scale: 0, 
    opacity: 0,
    transition: { duration: 0.15 }
  }
};

// Question navigation animations
export const questionSlideTransition: Variants = {
  initial: (direction: number) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0
  }),
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      x: { type: "spring", stiffness: 300, damping: 30 },
      opacity: { duration: 0.2 }
    }
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
    transition: { duration: 0.3 }
  })
};

// Loading spinner animation
export const spinnerTransition = {
  animate: {
    rotate: 360,
    transition: {
      duration: 1,
      repeat: Infinity,
      ease: "linear"
    }
  }
};

// Pulse animation for notifications
export const pulseTransition = {
  animate: {
    scale: [1, 1.05, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

// Skeleton loading animation
export const skeletonTransition = {
  animate: {
    backgroundPosition: ["200% 0", "-200% 0"],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: "linear"
    }
  }
};

// Success checkmark animation
export const checkmarkTransition: Variants = {
  initial: { 
    pathLength: 0,
    opacity: 0
  },
  animate: { 
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration: 0.5, ease: "easeInOut" },
      opacity: { duration: 0.3 }
    }
  }
};

// Error shake animation
export const shakeTransition = {
  animate: {
    x: [0, -10, 10, -10, 10, 0],
    transition: {
      duration: 0.5,
      ease: "easeInOut"
    }
  }
};

// Floating animation
export const floatTransition = {
  animate: {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

// Custom spring configurations
export const springConfig = {
  gentle: { stiffness: 150, damping: 20 },
  normal: { stiffness: 300, damping: 30 },
  stiff: { stiffness: 500, damping: 35 },
  wobbly: { stiffness: 150, damping: 10 }
};

// Animation delays
export const getStaggerDelay = (index: number, baseDelay: number = 0.05) => ({
  transition: { delay: index * baseDelay }
});

// Responsive animations
export const getResponsiveTransition = (isMobile: boolean): Variants => ({
  initial: { opacity: 0, y: isMobile ? 50 : 20 },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: isMobile ? 0.4 : 0.3,
      ease: 'easeOut'
    }
  },
  exit: { 
    opacity: 0, 
    y: isMobile ? -50 : -20,
    transition: { duration: 0.2 }
  }
});

// Gesture animations
export const dragConstraints = {
  top: -50,
  left: -50,
  right: 50,
  bottom: 50
};

export const dragTransition = {
  bounceStiffness: 600,
  bounceDamping: 20
};